import { useCallback, useEffect, useRef, useState } from 'react';
import './GoldenQuest.css';
import { countdownLabel, fmtDay, fmtDayLong, fmtDayOrdinal, fmtMonDayOrdinal } from './campaign';
import {
  fetchGoldenQuest, markStorySeen, preRegister, setReminder,
  markLiveIntroSeen, markChestCelebrated, openGem as openGemApi, mapGem,
} from './api';
import {
  goHome, shareWithFriends, loadCard, offersBrands, lumpsumScreen,
  getSessionToken, listenForSession,
} from '../Helper/native';
import Story from './components/Story';
import PreReg from './components/PreReg';
import GoLive from './components/GoLive';
import Celebrate from './components/Celebrate';
import Rewards from './components/Rewards';
import Hero from './components/Hero';
import StreakCard from './components/StreakCard';
import GrandPrizes from './components/GrandPrizes';
import LinksSection from './components/LinksSection';
import ShareCard from './components/ShareCard';
import StickyBar from './components/StickyBar';
import Toast from './components/Toast';
import Skeleton from './components/Skeleton';
import { PrizesList, TermsList, WinnersList } from './components/Sheets';
import { Lantern, SectionTitle, Sheet, TickerBand, SHEET_EXIT_MS } from './components/Shared';

/* The design is laid out on a fixed 375px stage (every offset in the CSS is
   absolute). Scale the stage to the device width with CSS `zoom`, capped so
   tablets / desktop get a centred phone-sized column instead of a giant one. */
const STAGE_W = 375;
const STAGE_MAX_W = 430;
const measureStage = () => {
  const zoom = Math.min(window.innerWidth || STAGE_W, STAGE_MAX_W) / STAGE_W;
  /* viewport height in stage px — the full-screen overlays are sized with it */
  return { zoom, stageHeight: Math.round((window.innerHeight || 812) / zoom) };
};

function useStage() {
  const [stage, setStage] = useState(measureStage);
  useEffect(() => {
    const onResize = () => setStage(measureStage());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return stage;
}

/* Hero dims and recedes as the streak sheet rides over it (prototype
   heroScroll). The prototype also slid the whole hero down with the scroll,
   but on short screens that let the sheet overtake "View all prizes" before
   it could clear the sticky footer — so the content scrolls with the page
   and only the atmosphere (backdrop, lanterns) lags, via --gq-lag. Styles are
   written straight to the node so scrolling doesn't re-render. */
function useHeroParallax(heroRef, zoom) {
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const hero = heroRef.current;
      if (!hero) return;
      const y = window.scrollY / zoom; // document scroll is unzoomed; the hero's px are zoomed
      const k = Math.min(y / 380, 1);
      hero.style.opacity = (1 - k * 0.62).toFixed(3);
      hero.style.transform = `scale(${(1 - k * 0.04).toFixed(3)})`;
      hero.style.setProperty('--gq-lag', `${(y * 0.32).toFixed(1)}px`);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [heroRef, zoom]);
}

/* live "9 Days 8 Hrs 27 Mins" until launch */
function useCountdown(launch) {
  const [label, setLabel] = useState(() => countdownLabel(launch));
  useEffect(() => {
    setLabel(countdownLabel(launch));
    const id = setInterval(() => setLabel(countdownLabel(launch)), 1000);
    return () => clearInterval(id);
  }, [launch]);
  return label;
}

/* native bridge calls throw outside the app webview; swallow so the page
   stays usable when previewed in a browser */
const native = (fn) => {
  try { fn(); } catch (e) { console.log('NATIVE BRIDGE UNAVAILABLE', e); }
};

/* The hub's entrance (hero drops in, tiles and cards rise) plays whenever
   the hub comes into view: on load, and again when a full-screen page
   (story, pre-registration, rewards) hands back to it — the prototype's
   `.hub.enter`, held for 1.4s. */
const ENTER_MS = 1400;
function useHubEnter(ready, covered) {
  const [enter, setEnter] = useState(false);
  useEffect(() => {
    if (!ready || covered) return undefined;
    setEnter(true);
    const t = setTimeout(() => setEnter(false), ENTER_MS);
    return () => clearTimeout(t);
  }, [ready, covered]);
  return enter;
}

/* A shimmering skeleton of the hub while the campaign loads (same stage scale
   and offsets as the real hub, so it drops into place), or a retry when the
   request fails. */
function LoadState({ failed, onRetry, zoom, stageHeight }) {
  if (!failed) {
    return (
      <div className="gq-root">
        <div className="gq-page" style={{ zoom }}>
          <Skeleton stageHeight={stageHeight} />
        </div>
      </div>
    );
  }
  return (
    <div className="gq-root">
      <div className="gq-state">
        <p>Couldn't load The Golden Quest right now.</p>
        <button type="button" className="gq-retry" onClick={onRetry}>Retry</button>
      </div>
    </div>
  );
}

const NO_SHEET = { kind: null, open: false };

/* The Golden Quest (/goldenQuest), opened from the campaign banner in the app.
   Flow: fetch the campaign → the "How to play" story (first open, when
   enabled) → pre-registration (pre-launch, not yet registered) or the
   go-live interstitial (first open after launch) → any chest celebration
   still due → the hub. From the hub: the Gems / Chests tiles open the
   rewards page, the prize / terms / winners links open bottom sheets,
   "How to play" replays the story and "Claim Now" opens pre-registration.
   Everything comes from the campaign API (see api.js / mockResponse.js). */
export default function GoldenQuest() {
  const [status, setStatus] = useState('loading'); // loading | ready | hidden | failed
  const [model, setModel] = useState(null);        // mapped resp.goldenQuest
  const [story, setStory] = useState(null);        // null | 'open' (first open) | 'howto' (from the hub)
  const [prereg, setPrereg] = useState(false);     // pre-registration screen over the hub
  const [golive, setGolive] = useState(false);     // "festival is open" interstitial
  const [rewards, setRewards] = useState(null);    // null | 'gems' | 'chests' (rewards page + tab)
  const [sheet, setSheet] = useState(NO_SHEET);    // { kind: prizes | terms | winners, open }
  const [registered, setRegistered] = useState(false); // pre-registration Gems claimed
  const [notify, setNotify] = useState(false);     // launch reminder set
  const [gemsList, setGemsList] = useState([]);    // the user's Gems (wallet.gems, mapped)
  const [chestsList, setChestsList] = useState([]); // the user's Gold Chests (wallet.chests, mapped)
  const [toast, setToast] = useState({ msg: '', gold: false, show: false });
  const [gemsFlash, setGemsFlash] = useState(false); // Gems tile lights up after the free Gems land
  const toastTimer = useRef(null);
  const flashTimers = useRef([]);
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);

  const { zoom, stageHeight } = useStage();
  const heroRef = useRef(null);
  useHeroParallax(heroRef, zoom);
  const countdown = useCountdown(model ? model.launch : undefined);
  /* full-screen pages that replace the hub (not the celebrate / go-live dialogs over it) */
  const pageCovered = !!story || prereg || !!rewards;
  const enter = useHubEnter(status === 'ready', pageCovered);

  const load = useCallback(() => {
    let alive = true;
    setStatus('loading');
    /* Acquire the access token the same way the other campaign pages do, so a
       native deep-link straight here is authenticated before we fetch: capture
       it from the URL and from the native session bridge, then persist it to
       sessionStorage — the single source of truth apiFetch reads. */
    const url = new URL(window.location.href);
    const tokenHeader = url.searchParams.get('token');
    const refreshToken = url.searchParams.get('refreshToken');
    if (tokenHeader) sessionStorage.setItem('AccessToken', tokenHeader);
    if (refreshToken) sessionStorage.setItem('RefreshToken', refreshToken);
    getSessionToken(); // also reads window.__GOSATS_SESSION__ injected by native

    fetchGoldenQuest()
      .then((m) => {
        if (!alive) return;
        if (!m) { setStatus('hidden'); return; }
        setModel(m);
        setRegistered(m.registered);
        setNotify(m.notify);
        setGemsList(m.gems);
        setChestsList(m.chests);
        setStory(m.storyEnabled && !m.storySeen ? 'open' : null); // story first on a fresh open
        setGolive(m.phase === 'live' && !m.liveIntroSeen);        // then the go-live interstitial, once
        setPrereg(false);
        setRewards(null);
        setSheet(NO_SHEET);
        setStatus('ready');
      })
      .catch((err) => {
        console.error('campaign fetch failed:', err);
        if (alive) setStatus('failed');
      });
    return () => { alive = false; };
  }, []);

  useEffect(load, [load]);

  /* The native app can inject the session asynchronously (postMessage) after
     mount; if the first load failed for lack of a token, run it again. */
  useEffect(() => listenForSession(() => { if (statusRef.current === 'failed') load(); }), [load]);

  /* a chest the user hasn't been shown yet → celebrate once the hub is clear */
  const pendingChest = model && model.phase === 'live' && !story && !prereg && !golive && !rewards && !sheet.kind
    ? chestsList.find((c) => !c.celebrated) || null
    : null;

  /* the hub underneath must not scroll while something covers it */
  const overlay = !!story || prereg || golive || !!rewards || !!pendingChest;
  const covered = overlay || !!sheet.kind;
  useEffect(() => {
    document.body.classList.toggle('gq-locked', covered);
    return () => document.body.classList.remove('gq-locked');
  }, [covered]);

  const showToast = useCallback((msg, gold = false) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, gold, show: true });
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2200);
  }, []);
  useEffect(() => () => { clearTimeout(toastTimer.current); flashTimers.current.forEach(clearTimeout); }, []);

  /* ---- story ---- */
  const openStory = useCallback(() => setStory('howto'), []);
  const endStory = useCallback(() => {
    if (story === 'open') {
      markStorySeen().catch(() => {});
      /* first open before launch without the free Gems → pre-register next */
      if (!registered && model && model.phase === 'pre') setPrereg(true);
    }
    setStory(null);
  }, [story, registered, model]);

  /* ---- pre-registration ---- */
  const openPrereg = useCallback(() => setPrereg(true), []);
  const closePrereg = useCallback(() => setPrereg(false), []);
  /* the claim carries the "Remind me when it starts" toggle as `notify` */
  const claimPrereg = useCallback((remind) => preRegister({ notify: remind }), []);
  /* `res` is the API's answer: the Gems it reserved (with the ids OPEN_GEM
     accepts from launch) and the reminder it stored */
  const donePrereg = useCallback((remind, res) => {
    const launchMs = model ? model.launch.getTime() : Date.now();
    const reserved = res && Array.isArray(res.gems) && res.gems.length
      ? res.gems.map(mapGem)
      : Array.from({ length: model ? model.preRegGems : 0 }, (_, i) => mapGem({ id: `prereg-${i + 1}`, source: 'PRE_REG', earnedAt: launchMs }, i));
    setRegistered(true);
    setNotify(res && typeof res.reminderSet === 'boolean' ? res.reminderSet : remind);
    /* the reserved Gems appear in the wallet right away; they unlock at launch */
    setGemsList((list) => (list.some((g) => g.source === 'PRE_REG') ? list : reserved.concat(list)));
    setPrereg(false);
    /* once the hub is back: the Gems count bumps, the tile flashes gold and the toast confirms */
    flashTimers.current.forEach(clearTimeout);
    flashTimers.current = [
      setTimeout(() => {
        setGemsFlash(true);
        showToast(`${reserved.length} Gems reserved · they unlock on ${fmtDay(new Date(launchMs))}`, true);
      }, 700),
      setTimeout(() => setGemsFlash(false), 1400),
    ];
  }, [model, showToast]);
  /* a decline carries the backend's reason (e.g. pre-registration has closed) */
  const failPrereg = useCallback((err) => {
    console.error('pre-registration failed:', err);
    showToast(err && err.refused ? err.message : 'Something went wrong, please try again');
  }, [showToast]);

  const toggleNotify = useCallback(() => {
    const on = !notify;
    setNotify(on);
    showToast(on ? "We'll remind you at 12:00 AM on 18 Oct" : 'Reminder removed');
    setReminder(on).catch((err) => {
      console.error('reminder update failed:', err);
      setNotify(!on);
      showToast('Something went wrong, please try again');
    });
  }, [notify, showToast]);

  /* ---- go-live interstitial ---- */
  const doneGolive = useCallback(() => {
    setGolive(false);
    markLiveIntroSeen().catch(() => {});
  }, []);

  /* ---- chest celebration ---- */
  const doneCelebrate = useCallback(() => {
    if (!pendingChest) return;
    const { id } = pendingChest;
    setChestsList((list) => list.map((c) => (c.id === id ? { ...c, celebrated: true } : c)));
    markChestCelebrated(id).catch(() => {});
  }, [pendingChest]);

  /* ---- rewards page ---- */
  const openRewards = useCallback((tab) => setRewards(tab), []);
  const closeRewards = useCallback(() => setRewards(null), []);
  /* the API decides the reward; the wallet updates as soon as it answers and
     the page animates the reveal */
  const openGem = useCallback(async (gemId) => {
    const { reward, openedAt: at } = await openGemApi(gemId);
    setGemsList((list) => list.map((g) => (g.id === gemId ? { ...g, opened: { at, reward } } : g)));
    showToast(`${reward.name} · ${reward.detail}`, true);
    return { reward, at };
  }, [showToast]);
  /* a decline carries the backend's reason (e.g. "Gems unlock on 18 Oct") */
  const failOpenGem = useCallback((err) => {
    console.error('open gem failed:', err);
    showToast(err && err.refused ? err.message : "Couldn't open that Gem, please try again");
  }, [showToast]);

  /* ---- bottom sheets ---- */
  const openSheet = useCallback((kind) => setSheet({ kind, open: true }), []);
  const closeSheet = useCallback(() => setSheet((s) => ({ ...s, open: false })), []);
  /* keep the content mounted while it slides out */
  useEffect(() => {
    if (sheet.kind && !sheet.open) {
      const t = setTimeout(() => setSheet(NO_SHEET), SHEET_EXIT_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [sheet]);

  const copyCode = useCallback(() => {
    const code = model ? model.referralCode : '';
    if (window.navigator && window.navigator.clipboard) {
      window.navigator.clipboard.writeText(code).catch(() => {});
    }
    showToast(`Code ${code} copied`);
  }, [model, showToast]);

  const share = useCallback(() => {
    if (model) native(() => shareWithFriends(model.shareMessage));
  }, [model]);
  const back = useCallback(() => native(goHome), []);

  /* dispatcher for the streak CTA actions the API sends */
  const runAction = useCallback((action) => {
    switch (action) {
      case 'ADD_MONEY': native(loadCard); break;                    // native card recharge
      case 'VOUCHERS': native(offersBrands); break;                 // native Brands screen
      case 'BUY_GOLD': native(() => lumpsumScreen(null, true)); break; // native lumpsum gold purchase
      case 'REFER': share(); break;                                 // native share sheet
      default: break; // unknown actions are no-ops
    }
  }, [share]);

  /* campaign off → nothing to show */
  if (status === 'hidden') return null;
  if (status !== 'ready' || !model) return <LoadState failed={status === 'failed'} onRetry={load} zoom={zoom} stageHeight={stageHeight} />;

  const { phase, preRegGems, referralCode, streaks, grandPrizes, faqs, terms, draw, storyEnabled } = model;
  const pre = phase === 'pre';
  const ended = phase === 'ended';
  const unopened = gemsList.filter((g) => !g.opened).length;
  const { drawOn } = grandPrizes;
  const drawDay = fmtDayLong(drawOn);

  let pill;
  if (pre) pill = `Campaign starts on ${fmtDayOrdinal(model.launch)}`;
  else if (ended) pill = `The quest ended · Chests opened on ${fmtDay(drawOn)}`;
  else pill = `The quest ends ${fmtMonDayOrdinal(model.end)}`;

  let sheetContent = null;
  if (sheet.kind === 'prizes') sheetContent = <PrizesList prizes={grandPrizes} drawOn={drawOn} />;
  else if (sheet.kind === 'terms') sheetContent = <TermsList terms={terms} />;
  else if (sheet.kind === 'winners' && draw) sheetContent = <WinnersList draw={draw} total={grandPrizes.total} />;

  return (
    <div className="gq-root">
      <div className="gq-page" style={{ zoom }} aria-hidden={covered}>
        <div className={`gq-hub${enter ? ' gq-enter' : ''}`} data-phase={phase}>
          <div className="gq-hub-content">
            <Hero
              phase={phase}
              registered={registered}
              preRegGems={preRegGems}
              gems={gemsList.length}
              unopened={unopened}
              chests={chestsList.length}
              countdown={countdown}
              launch={model.launch}
              drawOn={drawOn}
              heroRef={heroRef}
              showHowTo={storyEnabled}
              flash={gemsFlash}
              onBack={back}
              onShare={share}
              onHowTo={openStory}
              /* before launch the Gems tile is the "Claim 5 Gems" call: it opens pre-registration */
              onGems={pre && !registered ? openPrereg : () => openRewards('gems')}
              onChests={() => openRewards('chests')}
              onViewPrizes={() => openSheet('prizes')}
            />

            <div className="gq-body">
              <TickerBand />
              <div className="gq-upper">
                <div className="gq-mid">
                  <Lantern pos="l5" />
                  <Lantern pos="l6" chain="chain2" />
                  <Lantern pos="l7" mirror />
                  <Lantern pos="l8" chain="chain2" mirror />
                </div>
                <div className="gq-pill"><span>{pill}</span></div>
                <SectionTitle variant="streaks">Your Streaks</SectionTitle>
                <div className="gq-rule">
                  {ended
                    ? 'Streaks are closed. Your chests were part of the draw.'
                    : 'Each streak and Golden chest can only be completed once. Referrals keep earning Gems.'}
                </div>
                <div className="gq-cards">
                  {streaks.map((s) => (
                    <StreakCard
                      key={s.key}
                      streak={s}
                      phase={phase}
                      progress={s.progress}
                      completed={s.completed}
                      extraGems={s.extraGems}
                      onCta={() => runAction(s.action)}
                    />
                  ))}
                </div>
              </div>

              <GrandPrizes prizes={grandPrizes} onViewAll={() => openSheet('prizes')} />
              <LinksSection faqs={faqs} onTerms={() => openSheet('terms')} />
              <ShareCard code={referralCode} onCopy={copyCode} onShare={share} />
            </div>
          </div>
        </div>

        {(pre || (ended && draw)) && (
          <StickyBar
            phase={phase}
            grandTotal={grandPrizes.total}
            launch={model.launch}
            drawOn={drawOn}
            notify={notify}
            onToggle={toggleNotify}
            onWinners={() => openSheet('winners')}
          />
        )}
      </div>

      {/* bottom sheets: outside the (aria-hidden) page, fixed over the hub */}
      {sheet.kind && sheetContent && (
        <div style={{ zoom }}>
          <Sheet open={sheet.open} label={sheet.kind === 'prizes' ? 'Golden Chest Prizes' : (sheet.kind === 'terms' ? 'Terms & Conditions' : 'Grand draw results')} stageHeight={stageHeight} onClose={closeSheet}>
            {sheetContent}
          </Sheet>
        </div>
      )}

      {rewards && !story && (
        <Rewards
          phase={phase}
          tab={rewards}
          onTab={setRewards}
          gems={gemsList}
          chests={chestsList}
          grandTotal={grandPrizes.total}
          drawDay={drawDay}
          launch={model.launch}
          drawOn={drawOn}
          zoom={zoom}
          stageHeight={stageHeight}
          onBack={closeRewards}
          onOpen={openGem}
          onError={failOpenGem}
        />
      )}

      {pendingChest && (
        <Celebrate
          chest={pendingChest}
          chestCount={chestsList.length}
          drawDay={drawDay}
          zoom={zoom}
          stageHeight={stageHeight}
          onDone={doneCelebrate}
        />
      )}

      {golive && !story && (
        <GoLive
          registered={registered}
          unopened={unopened}
          endDay={fmtDayLong(model.end)}
          zoom={zoom}
          stageHeight={stageHeight}
          onDone={doneGolive}
        />
      )}

      {prereg && !story && (
        <PreReg
          gems={preRegGems}
          remind
          zoom={zoom}
          stageHeight={stageHeight}
          onBack={closePrereg}
          onClaim={claimPrereg}
          onDone={donePrereg}
          onError={failPrereg}
        />
      )}

      {story && (
        <Story
          drawLabel={fmtMonDayOrdinal(drawOn)}
          startsLabel={pre ? `Starts on ${fmtDayOrdinal(model.launch)}` : ''}
          zoom={zoom}
          stageHeight={stageHeight}
          onEnd={endStory}
        />
      )}

      {/* outside the (aria-hidden) page so it shows above, and is announced over, the overlays */}
      <div style={{ zoom }}>
        <Toast toast={toast} />
      </div>
    </div>
  );
}
